'use client';
import { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import useWindowSize from '@repo/ui/src/hooks/useWindowSize';
import buildLinkObj from '@repo/ui/src/functions/build-link';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { imageFullWidthXs, imageFullWidthSmAndUp } from '@style/variables';
import BrandDivider from '@components/BrandDivider';
import { classNames } from '@repo/ui/src/utils/classNames';
import type { MediaType } from '@repo/ui/src/types/image';
import type { LinkProps } from '@repo/ui/src/types/link';

interface LifestyleBannerProps {
  backgroundImage?: MediaType;
  title: string;
  subtitle?: string;
  description?: string;
  link?: LinkProps;
  alignment?: 'left' | 'right';
  verticalAlignment?: 'top' | 'bottom';
  cardStyle?: boolean;
  showDivider?: boolean;
}

const LifestyleBanner = ({
  backgroundImage,
  title,
  subtitle,
  description,
  link,
  alignment,
  verticalAlignment,
  cardStyle,
  showDivider,
}: LifestyleBannerProps) => {
  // Allow users to specify linebreak with \n in Amplience
  const splitTitle = title.split('\\n').map((x, i) => <div key={`${x}_${i}`}>{x}</div>);
  const [isServer, setIsServer] = useState(true);
  const VideoRendered = dynamic(import('@repo/ui/src/components/BackgroundVideo'), {
    ssr: isServer,
  });
  useEffect(() => setIsServer(false), []);

  const { width = 0 } = useWindowSize();
  const xsOnly = width > 0 && width < 600;
  const linkObj = buildLinkObj(link);
  const alignRight = !xsOnly && alignment === 'right';

  const IMAGE_META_SCHEMA = 'http://bigcontent.io/cms/schema/v1/core#/definitions/image-link';
  const VIDEO_META_SCHEMA = 'http://bigcontent.io/cms/schema/v1/core#/definitions/video-link';
  const FULL_TO_THEATER = 1920;

  let imgUrl: string | undefined;
  let isVideo: boolean | undefined;
  let vidWidth: number | undefined;

  if (backgroundImage && backgroundImage._meta?.schema === IMAGE_META_SCHEMA) {
    isVideo = false;
    imgUrl = buildImageSrc(
      backgroundImage,
      xsOnly ? imageFullWidthXs : imageFullWidthSmAndUp,
      xsOnly ? 3 : 4,
      xsOnly ? 5 : 3,
    );
  } else if (backgroundImage && backgroundImage._meta?.schema === VIDEO_META_SCHEMA) {
    isVideo = true;
    vidWidth = width > FULL_TO_THEATER ? FULL_TO_THEATER : width;
    if (!vidWidth) {
      vidWidth = xsOnly ? imageFullWidthXs : imageFullWidthSmAndUp;
    }
  }

  const getVerticalAlignmentClass = () => {
    if (verticalAlignment === 'top') return 'sm:justify-start';
    if (verticalAlignment === 'bottom') return 'sm:justify-end';
    return 'sm:justify-center';
  };

  const textColor = backgroundImage ? 'text-white' : 'text-neutral-800';

  return (
    <div
      className={classNames(
        'text-white relative overflow-hidden',
        imgUrl &&
          'bg-center bg-no-repeat bg-cover h-[450px] max-h-[800px] md:h-[80vh] md:min-h-[480px]',
        !imgUrl && 'min-h-[480px] w-full',
        cardStyle && 'card-style',
      )}
      style={imgUrl ? { backgroundImage: `url('${imgUrl}')` } : {}}
    >
      {isVideo && <VideoRendered video={backgroundImage!} width={vidWidth!} />}
      <div
        className={classNames(
          'absolute bottom-0 w-full max-w-full h-full flex flex-col items-center px-4 py-8 sm:p-12 justify-end',
          getVerticalAlignmentClass(),
          cardStyle ? 'lg:px-12' : 'lg:px-28',
          !cardStyle && [
            'black-overlay',
            alignRight
              ? '[background-image:linear-gradient(to_bottom,rgba(255,0,0,0),rgba(0,0,0,0.6))] sm:[background-image:linear-gradient(to_right,rgba(0,0,0,0),rgba(0,0,0,0.7))]'
              : '[background-image:linear-gradient(to_bottom,rgba(255,0,0,0),rgba(0,0,0,0.6))] sm:[background-image:linear-gradient(to_left,rgba(0,0,0,0),rgba(0,0,0,0.7))]',
          ],
        )}
      >
        <div
          className={classNames(
            'flex flex-col w-full max-w-screen-xl',
            alignRight ? 'items-end' : 'items-start',
          )}
        >
          <div className="w-full sm:w-1/2 md:w-5/12 lg:w-1/3 xl:w-1/4">
            <div
              className={classNames(
                cardStyle &&
                  'bg-[rgba(37,40,42,0.9)] rounded-[5px] px-6 py-8 sm:px-12 sm:py-10 lg:px-16 lg:py-14',
              )}
            >
              {title && (
                <h1
                  className={classNames(
                    'font-banner uppercase font-bold m-0',
                    'text-[2rem] leading-[1.8rem] sm:text-[3rem] sm:leading-[2.6rem]',
                    textColor,
                  )}
                >
                  {splitTitle}
                  {subtitle && <span className="font-normal subtitle">{subtitle}</span>}
                </h1>
              )}
              {showDivider && (
                <div className="mb-4 sm:mb-6">
                  <BrandDivider align="left" />
                </div>
              )}
              {description && (
                <p
                  className={classNames(
                    'tra-text-web-body text-xs sm:text-sm m-0',
                    showDivider ? 'mt-2' : 'mt-6',
                    textColor,
                  )}
                >
                  {description}
                </p>
              )}
              {linkObj?.text && (
                <div className="mt-12 sm:mt-8">
                  <Button
                    variant={ButtonVariant.secondary}
                    negative
                    as={Anchor}
                    full={xsOnly}
                    {...link}
                  >
                    {linkObj.text}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LifestyleBanner;
